import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@1trading/react';
import { Landmark } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { newLoan } from '@/services/api/loans';
import { runAuditedLoanMutation } from './run-audited-loan-mutation';

interface AddInstitutionDialogProps {
  onClose: () => void;
  /** Refetch loans after a successful add — this flow also bootstraps an unseeded environment */
  onMutated: () => void;
  /** HTTP 403 → the page-level Unauthorised Action dialog */
  onUnauthorised: () => void;
}

// Mounted only while open, so the fields reset on every open/close.
export const AddInstitutionDialog = ({ onClose, onMutated, onUnauthorised }: AddInstitutionDialogProps) => {
  const fieldId = useId();
  const [name, setName] = useState('');
  const [pid, setPid] = useState('');
  const trimmedName = name.trim();
  const trimmedPid = pid.trim();

  const handleAdd = async () => {
    if (trimmedName === '' || trimmedPid === '') return;
    const outcome = await runAuditedLoanMutation({
      title: `Confirm Add Institution ${trimmedName}`,
      description: `Proceeding with this action will add ${trimmedName} (PID: ${trimmedPid}) to the credit line management system. Your action here will be logged and will be auditable.`,
      actionLabel: `Add Institution ${trimmedName}`,
      // Server errors such as 'Client already has a loan' or 'User segment
      // not allowed to take out a loan' surface via the toast in this fallback path
      fallbackError: 'Failed to add institution',
      // Wire contract: pid/name here vs clientPID/clientName on updateLoan
      // (kept as-is); no `loans` — the BOP flow adds assets afterwards
      execute: (comment) => newLoan({ pid: trimmedPid, name: trimmedName, comment }),
    });
    if (outcome === 'unauthorised') {
      onClose();
      onUnauthorised();
      return;
    }
    if (outcome !== 'success') return;
    toast.success(`Successfully added institution ${trimmedName}`);
    onMutated();
    onClose();
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="size-5" />
            Add Institution to Credit Line management
          </DialogTitle>
          {/* newLoan never verifies the PID exists in the exchange — this copy
              is the only guard against a typo'd PID creating a dangling entry */}
          <DialogDescription>
            The Institution Name is a nickname for the institution, it does not have to match the institution's full name as onboarded. The PID must match an active institution in
            our system.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-name`}>Institution Name</Label>
            <Input id={`${fieldId}-name`} onChange={(event) => setName(event.target.value)} value={name} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-pid`}>PID</Label>
            <Input id={`${fieldId}-pid`} onChange={(event) => setPid(event.target.value)} value={pid} />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={trimmedName === '' || trimmedPid === ''} onClick={handleAdd} type="button" variant="primary">
            Add Institution
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
AddInstitutionDialog.displayName = 'AddInstitutionDialog';
